'use client';

import { Box, Tooltip } from '@mui/material';
import type { SparkJobStatus } from '@repo/api-gw-sdk';
import React from 'react';

interface SparkJobStatusConfig {
  title: string;
  iconColor: string;
  backgroundColor: string;
  icon: string;
  staticIcon?: string;
}

const sparkJobStatusConfig: Record<SparkJobStatus, SparkJobStatusConfig> = {
  SUBMITTED: {
    title: 'Submitted',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-upload-rounded',
  },
  PENDING: {
    title: 'Pending',
    iconColor: 'var(--mui-palette-custom-GREY-01)',
    backgroundColor: 'var(--mui-palette-custom-GREY-OPACITY)',
    icon: 'material-symbols-avg-pace-rounded',
  },
  SCHEDULED: {
    title: 'Scheduled',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-event-outline',
  },
  RUNNING: {
    title: 'Running',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'line-md-loading-loop',
    staticIcon: 'material-symbols-progress-activity',
  },
  SUCCESS: {
    title: 'Success',
    iconColor: 'var(--mui-palette-success-main)',
    backgroundColor: 'var(--mui-palette-success-opacity)',
    icon: 'material-symbols-check-circle-outline-rounded',
  },
  FAILED: {
    title: 'Failed',
    iconColor: 'var(--mui-palette-error-main)',
    backgroundColor: 'var(--mui-palette-error-opacity)',
    icon: 'material-symbols-cancel-outline',
  },
  CANCELLING: {
    title: 'Cancelling',
    iconColor: 'var(--mui-palette-warning-main)',
    backgroundColor: 'var(--mui-palette-warning-opacity)',
    icon: 'line-md-loading-loop',
    staticIcon: 'material-symbols-progress-activity',
  },
  CANCELLED: {
    title: 'Cancelled',
    iconColor: 'var(--mui-palette-custom-GREY-01)',
    backgroundColor: 'var(--mui-palette-custom-GREY-OPACITY)',
    icon: 'material-symbols-cancel-outline',
  },
  QUEUED: {
    title: 'Queued',
    iconColor: 'var(--mui-palette-custom-GREY-01)',
    backgroundColor: 'var(--mui-palette-custom-GREY-OPACITY)',
    icon: 'material-symbols-schedule-outline',
  },
};

export const SparkJobStatusCell = React.forwardRef<
  HTMLSpanElement,
  {
    status: SparkJobStatus;
    isStatic?: boolean;
    hideTooltip?: boolean;
  }
>(({ status, isStatic, hideTooltip }, ref) => {
  const config = sparkJobStatusConfig[status];
  if (!config) {
    return null;
  }

  return (
    <Tooltip title={hideTooltip ? undefined : config.title}>
      <Box
        ref={ref}
        component='span'
        className='rounded-full inline-flex p-[2px]'
        sx={{
          background: config.backgroundColor,
          '& i': {
            background: config.iconColor,
          },
        }}
      >
        <i
          className={`${isStatic && config.staticIcon ? config.staticIcon : config.icon} h-[20px] w-[20px]`}
        />
      </Box>
    </Tooltip>
  );
});

SparkJobStatusCell.displayName = 'SparkJobStatusCell';
